if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.form.querySelector("[name=id]").disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.minicart = document.querySelector("mini-cart");
        this.quickViewDrawer = this.closest("quick-view-drawer");
        this.eventContext = this.form.dataset.eventContext;
        this.hideErrors = this.dataset.hideErrors === "true";
        this.form.addEventListener("change", (event) => {
          const group = event.target.closest("[data-product-option-group]");
          if (group) {
            // Colour swatches: show the chosen colour's name beside the label.
            const selected = group.querySelector("[data-swatch-selected]");
            if (selected) {
              selected.textContent = event.target.value;
            }
          }
          if (event.target.matches("[data-product-option][aria-invalid]") || group?.hasAttribute("aria-invalid")) {
            this.validateProductOptions(false);
          }
        });
      }

      onSubmitHandler(event) {
        event.preventDefault();
        const submitButton = this.querySelector('[type="submit"]');
        if (submitButton.classList.contains("loading")) {
          return;
        }

        if (!this.validateProductOptions()) {
          return;
        }

        this.handleErrorMessage();

        submitButton.setAttribute("aria-disabled", true);
        submitButton.classList.add("loading");
        this.querySelector(".button-overlay-spinner").classList.remove("hidden");

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);
        // Optional text options (e.g. "Colour Code or VIN") left blank shouldn't add an empty property.
        for (const [name, value] of Array.from(formData.entries())) {
          if (/^properties\[.+\]$/.test(name) && typeof value === "string" && value.trim() === "") {
            formData.delete(name);
          }
        }
        const sections = this.minicart.getSectionsToRender().map((section) => section.section);
        const addonItems = this.getAddonItems();

        if (addonItems.length) {
          // Add-ons ticked (snippets/product-addons.liquid): add the main item and the
          // add-ons together as separate line items in one request.
          config.headers["Content-Type"] = "application/json";
          config.body = JSON.stringify({
            items: [this.getMainItem(formData), ...addonItems],
            sections,
            sections_url: window.location.pathname,
          });
        } else {
          formData.append("sections", sections);
          formData.append("sections_url", window.location.pathname);
          config.body = formData;
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message,
              });

              this.handleErrorMessage(response.description);
              this.minicart.updateSectionContents();
              return;
            }

            this.minicart.renderContents(response);

            if (window.themeSettings.redirectToCart) {
              location.href = window.routes.cart_url;
            } else {
              if (this.quickViewDrawer) {
                this.quickViewDrawer.close();
                // Immediately re-add the 'drawer-open' class to avoid
                // the drawer backdrop from flashing.
                document.body.classList.add("drawer-open");
                setTimeout(() => this.minicart.open(), 200);
              } else {
                this.minicart.open(submitButton);
              }
            }

            publish(PUB_SUB_EVENTS.cartItemAdd, {
              source: this.eventContext,
              items: response.items || [response],
            });
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            submitButton.classList.remove("loading");
            submitButton.removeAttribute("aria-disabled");
            this.querySelector(".button-overlay-spinner").classList.add("hidden");
          });
      }

      // Required option dropdowns (snippets/product-options.liquid). The form is novalidate,
      // so check them here and point the customer at the first one that's missing.
      validateProductOptions(focusFirstMissing = true) {
        // Disabled options are tied to an add-on that isn't ticked (product-addons.js), so skip them.
        // Dropdowns ([data-product-option]) need a value; swatch groups ([data-product-option-group])
        // need a ticked swatch.
        const fields = Array.from(
          this.form.querySelectorAll("[data-product-option]:not(:disabled), [data-product-option-group]:not(:disabled)"),
        );
        const hasValue = (field) =>
          field.matches("[data-product-option-group]") ? field.querySelector("input:checked") !== null : Boolean(field.value);
        const missing = fields.filter((field) => !hasValue(field));
        const error = this.form.querySelector("[data-product-options-error]");

        fields.forEach((field) => {
          if (hasValue(field)) {
            field.removeAttribute("aria-invalid");
          } else {
            field.setAttribute("aria-invalid", "true");
          }
        });
        if (error) {
          error.hidden = missing.length === 0;
        }
        if (missing.length && focusFirstMissing) {
          (missing[0].querySelector("input") || missing[0]).focus();
          missing[0].scrollIntoView({ behavior: "smooth", block: "center" });
        }
        return missing.length === 0;
      }

      getAddonItems() {
        return Array.from(this.form.querySelectorAll("[data-addon-variant-id]:checked")).map(
          (input) => ({ id: Number(input.dataset.addonVariantId), quantity: 1 }),
        );
      }

      getMainItem(formData) {
        const item = {
          id: Number(formData.get("id")),
          quantity: Math.max(1, parseInt(formData.get("quantity"), 10) || 1),
        };
        const properties = {};

        for (const [name, value] of formData.entries()) {
          const match = name.match(/^properties\[(.+)\]$/);
          if (match && typeof value === "string") {
            properties[match[1]] = value;
          }
        }

        if (Object.keys(properties).length) {
          item.properties = properties;
        }
        if (formData.get("selling_plan")) {
          item.selling_plan = formData.get("selling_plan");
        }

        return item;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) {
          return;
        }

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector(".product-form-error-message-wrapper");
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(".product-form-error-message");

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    },
  );
}
